/**
 * SignUp Component
 *
 * Registration form that creates a new user account via Firebase Authentication.
 * Collects email, password, and password confirmation. Performs client-side
 * validation (password match, minimum length) before calling the signup method
 * from AuthContext. On success, displays a confirmation message and redirects
 * to the login page after a short delay so the user can verify their email.
 *
 * Route: /signup (AuthRestrictedRoute, only for non-authenticated users)
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import './SignUp.css';

/**
 * Client-side password policy check.
 *
 * Single source of truth for the password rules; these MUST mirror the
 * Firebase Authentication password policy exactly so the client never accepts
 * a password Firebase would reject (no client/server drift). Returns an array
 * of human-readable messages, one per FAILED rule; an empty array means the
 * password satisfies every rule. Exported so the rules can be unit-tested in
 * isolation.
 */
export function validatePassword(password) {
    const errors = [];

    if (password.length < 8) {
        errors.push('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
        errors.push('Password must include at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
        errors.push('Password must include at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
        errors.push('Password must include at least one number');
    }

    return errors;
}

export default function SignUp() {
    const { ecosystem, isPlatform } = useEcosystem();
    // A RILE Connect account is platform-level (one identity, many ecosystem
    // memberships). In tenant mode we frame signup around joining that
    // ecosystem; at the apex we frame it around the platform network.
    const signupContext = isPlatform
        ? 'the RILE Connect network'
        : ecosystem?.name
          ? `the ${ecosystem.name} network`
          : 'the RILE Connect network';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    // Per-rule password policy errors, shown inline beneath the password field.
    const [passwordErrors, setPasswordErrors] = useState([]);

    const { signup } = useAuth();
    const navigate = useNavigate();

    // Validate inputs client-side, then create the Firebase account
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setPasswordErrors([]);

        // Client-side password validation
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Enforce the full Firebase password policy client-side so the user
        // sees specific, actionable messages without a round-trip to Firebase.
        const pwErrors = validatePassword(password);
        if (pwErrors.length > 0) {
            setPasswordErrors(pwErrors);
            return;
        }

        setLoading(true);

        try {
            await signup(email, password);
            setMessage(
                'Account created! Please check your email to verify your account before logging in.'
            );
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists');
            } else if (err.code === 'auth/weak-password') {
                setError(
                    'Password is too weak. Please use a stronger password'
                );
            } else {
                setError('Failed to create account. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Sign Up"
                description={`Create a RILE Connect account to join ${signupContext}.`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Sign Up', path: '/signup' },
                ]}
            />
            <Container className="signup-container py-5">
                <Row className="justify-content-center">
                    <Col md={6} lg={5}>
                        <Card className="shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="text-center text-primary mb-4">
                                    Create Account
                                </h2>

                                {/* Feedback alerts: validation errors or success */}
                                {error && (
                                    <Alert variant="danger">{error}</Alert>
                                )}
                                {message && (
                                    <Alert variant="success">{message}</Alert>
                                )}

                                {/* Registration form fields */}
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                // Clear stale policy errors as
                                                // the user edits the field.
                                                setPasswordErrors([]);
                                            }}
                                            placeholder="Create a password"
                                            required
                                            isInvalid={
                                                passwordErrors.length > 0
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            <ul className="mb-0 ps-3">
                                                {passwordErrors.map((msg) => (
                                                    <li key={msg}>{msg}</li>
                                                ))}
                                            </ul>
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            Must be at least 8 characters and
                                            include an uppercase letter, a
                                            lowercase letter, and a number.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Confirm Password
                                        </Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) =>
                                                setConfirmPassword(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Confirm your password"
                                            required
                                        />
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100 rounded-pill"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Creating Account...'
                                            : 'Sign Up'}
                                    </Button>
                                </Form>

                                {/* Link to login for returning users */}
                                <div className="text-center mt-4">
                                    <p>
                                        Already have an account?{' '}
                                        <Link
                                            to="/login"
                                            className="text-primary fw-medium"
                                        >
                                            Login
                                        </Link>
                                    </p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
